/**
 * Which cycle intervals may have their horizontal band drawn yet.
 *
 * <h2>The problem</h2>
 * A cycle's vertical lines are a TIME projection: they come from the cycle arithmetic and can be
 * drawn the moment the study runs, whether or not the bars between them have happened. The
 * horizontal band is a PRICE measurement: it is the high and low of the bars spanning the interval,
 * so it cannot exist until those bars do.
 *
 * <h2>Why suppressing it is not optional</h2>
 * In Simulate Mode the unrevealed bars are not absent — they are flat one-price bars at the frozen
 * close. Measuring a band across them does not fail; it returns a band of zero height at that
 * price, drawn in the same pen as a real one. The reader would be looking at a confident,
 * meaningless rectangle, and a study that says nothing would be indistinguishable from one that
 * says something. Suppression is what keeps "not yet known" visibly different from "known and
 * narrow".
 *
 * <h2>The rule</h2>
 * An interval's band appears once the revealed boundary reaches its END bar, and disappears again
 * if the reader steps back before it. Reaching the end is the condition because the band is the
 * extreme over the whole interval: with one bar still hidden, the true high or low may be the one
 * still hidden.
 */
import { guideLevels } from './bandGuides';

const isNum = v => typeof v === 'number' && Number.isFinite(v);

/**
 * The two dates of a study interval, whatever shape the endpoint sent it in.
 *
 * <p>The study endpoints do not agree, and there is no reason a caller should have to know which
 * one it is talking to: `/planet-retro-direct` answers with `{startDate, endDate}` objects, and
 * `/planet-in-sign` answers with two-element ARRAYS. Older studies also use `{date1, date2}`.
 *
 * <p>This exists because that difference broke something silently. The cycle buttons were
 * repointed from the first endpoint to the second, and the consumer read `pair.startDate ||
 * pair.date1` — both `undefined` on an array — so every interval was skipped by the guard directly
 * beneath it and the buttons drew nothing at all. No error, no empty-result message, no failed
 * request: a successful call whose every row was discarded one line later.
 *
 * <p>Which is why it is one exported function rather than a branch at each call site. There were
 * two call sites and only one of them handled arrays; the working one is not much use as
 * documentation when the broken one is somewhere else.
 *
 * @param {Array<string>|{startDate?:string,endDate?:string,date1?:string,date2?:string}} pair
 * @returns {[string|undefined, string|undefined]} start then end, unvalidated
 */
export function pairDates(pair) {
  if (Array.isArray(pair)) return [pair[0], pair[1]];
  if (!pair || typeof pair !== 'object') return [undefined, undefined];
  return [pair.startDate || pair.date1, pair.endDate || pair.date2];
}

/**
 * Cycle pairs resolved to bar indices on a given axis.
 *
 * <p>Both ends must land on the axis. A pair with an unresolvable end is dropped rather than
 * clamped: clamping would move the interval to a boundary it was never about, and the band would
 * then measure the wrong stretch of price.
 *
 * @param {Array<Array<string>|{startDate?:string,endDate?:string,date1?:string,date2?:string}>} pairs
 * @param {(date: string) => number} indexOf resolves a date to a bar index, or -1
 * @returns {Array<{startIndex:number,endIndex:number,startDate:string,endDate:string}>}
 */
export function intervalIndices(pairs, indexOf) {
  if (!Array.isArray(pairs) || typeof indexOf !== 'function') return [];
  const out = [];
  pairs.forEach((pair) => {
    const [startDate, endDate] = pairDates(pair);
    if (!startDate || !endDate) return;
    const a = indexOf(startDate);
    const b = indexOf(endDate);
    if (!isNum(a) || !isNum(b) || a < 0 || b < 0) return;
    // Normalised, so a pair supplied end-first still describes the same stretch.
    out.push({
      startIndex: Math.min(a, b),
      endIndex: Math.max(a, b),
      startDate: a <= b ? startDate : endDate,
      endDate: a <= b ? endDate : startDate,
    });
  });
  return out.sort((x, y) => x.startIndex - y.startIndex);
}

/**
 * The intervals whose bands may be drawn at a given reveal boundary.
 *
 * @param {Array<object>} intervals from intervalIndices
 * @param {number|null} revealIndex the last revealed bar, or null when nothing is masked
 * @returns {{visible: Array<object>, pending: Array<object>}}
 */
export function gateIntervals(intervals, revealIndex) {
  const all = Array.isArray(intervals) ? intervals : [];
  // Not simulating: nothing is hidden, so every band is measurable. Null rather than a large
  // sentinel, because "no boundary" and "a boundary past the end" are different states and only
  // one of them should survive a later change to how the boundary is stored.
  if (!isNum(revealIndex)) return { visible: all, pending: [] };

  const visible = [];
  const pending = [];
  all.forEach((interval) => {
    // `>=`, not `>`: the end bar itself is revealed at the boundary, and it is part of the span the
    // band measures. Using `>` would hold every band back by one step, which reads as the feature
    // being late rather than as a rule.
    if (interval.endIndex <= revealIndex) visible.push(interval);
    else pending.push(interval);
  });
  return { visible, pending };
}

/**
 * A short account of what is drawn and what is waiting, for the status line.
 *
 * <p>Stated rather than left to inference: a reader who cannot see a band needs to know whether it
 * is suppressed or simply absent, and those look identical on a chart.
 */
export function gateSummary(label, visible, pending) {
  const v = Array.isArray(visible) ? visible.length : 0;
  const p = Array.isArray(pending) ? pending.length : 0;
  if (p === 0) return `${label}: ${v} price band${v === 1 ? '' : 's'}.`;
  return `${label}: ${v} price band${v === 1 ? '' : 's'}, `
    + `${p} waiting for the interval to close.`;
}

/** Positions within the chart's own bar array — ECharts order, not OHLC order. */
const BAR_LOW = 2;
const BAR_HIGH = 3;

/**
 * The horizontal band for each interval.
 *
 * <h2>Measured over the interval, drawn to the NEXT one</h2>
 * The high and low come from the bars inside the cycle; the rectangle then runs forward to where
 * the next cycle begins. That is the reading: the range this cycle established stays in force until
 * the next cycle replaces it, so the level is visible over the stretch it is meant to apply to
 * rather than only over the bars that produced it.
 *
 * <p>The two spans are deliberately different, which is why both are named here. Measuring over the
 * drawn span would fold the following stretch of price into the level it is supposed to be tested
 * against — the band would then always contain the price action it was drawn over, and could never
 * be seen to fail.
 *
 * <h2>The last one PROJECTS FORWARD</h2>
 * With no next cycle there is nothing to run to, so the final band takes `projectLastTo` — an
 * index past the last price bar — and runs there instead of stopping on its own right edge. That
 * band is the only one still in force: every earlier one was replaced by the cycle that followed
 * it, and this one has not been. Stopping it at its own end drew the one level a reader is
 * actually trading against as though it had already expired.
 *
 * <p>This is NOT the old "run to the last bar of the chart" behaviour, which was removed as the
 * "random length": that edge was set by how much history happened to be loaded, so the same study
 * drew a different rectangle on a two-year chart and a thirty-year one. A fixed number of bars
 * past the last real bar is the same distance on both.
 *
 * <p>Omitted, the final band stops at its own end as before — so a caller that does not ask for a
 * projection cannot accidentally get one.
 *
 * @param {Array<object>} visible the intervals whose bands may be drawn (see gateIntervals)
 * @param {Array<object>} all every interval of the run, used only to find each one's successor
 * @param {Array<string>} dates the chart's bar dates, INCLUDING any future padding to project into
 * @param {Array<Array<number>>} ohlc
 * @param {number|null} opts.projectLastTo bar index the final band runs to, when it has no successor
 * @param {number|null} opts.clipRightTo the last REVEALED bar, past which no band may be drawn
 */
export function buildCycleBands(visible, all, dates, ohlc,
  { projectLastTo = null, clipRightTo = null } = {}) {
  if (!Array.isArray(visible) || !Array.isArray(dates) || !Array.isArray(ohlc)) return [];
  // Successors come from the WHOLE run, not from the visible subset. In Simulate Mode the next
  // cycle's vertical line is already drawn - it is a time projection, known before its bars are -
  // so a band must reach it even while that cycle is itself still pending.
  const ordered = (Array.isArray(all) && all.length > 0 ? [...all] : [...visible])
    .sort((a, b) => a.startIndex - b.startIndex);

  const bands = [];
  visible.forEach((interval) => {
    const lo = Math.min(interval.startIndex, interval.endIndex);
    const hi = Math.max(interval.startIndex, interval.endIndex);
    if (!isNum(lo) || !isNum(hi) || lo < 0 || hi >= dates.length) return;

    let yMin = Infinity;
    let yMax = -Infinity;
    for (let k = lo; k <= hi; k++) {
      const bar = ohlc[k];
      if (!bar) continue;
      const low = bar[BAR_LOW];
      const high = bar[BAR_HIGH];
      if (isNum(low)) yMin = Math.min(yMin, low);
      if (isNum(high)) yMax = Math.max(yMax, high);
    }
    // An interval of padding bars has no range to draw.
    if (!isNum(yMin) || !isNum(yMax)) return;

    const next = ordered.find(o => o.startIndex > lo);
    // Never shorter than the interval it measures. Cycles can overlap, and a right edge inside its
    // own span would draw a band narrower than the bars it was taken from.
    //
    // The projection applies ONLY where there is no successor. A band that runs to the next cycle
    // is bounded by that cycle, and extending it as well would have it overlap the band that
    // replaced it - two levels claiming the same stretch.
    let rawRight;
    if (next) rawRight = Math.max(hi, next.startIndex);
    else if (isNum(projectLastTo)) rawRight = Math.max(hi, projectLastTo);
    else rawRight = hi;
    // Clamped to the axis, which is why `dates` must be the padded array when projecting: clipped
    // against the real bars the projection would render as no projection at all.
    //
    // ...and to the last REVEALED bar when one is in force. A band is a horizontal mark -- it is
    // bounded in price -- so it may not be drawn over a column that has none. Both branches
    // above reach past the playhead on a replay: a band with a successor runs to that
    // successor's start, and the last one projects a year forward into empty canvas.
    const ceiling = isNum(clipRightTo)
      ? Math.min(dates.length - 1, clipRightTo)
      : dates.length - 1;
    const right = Math.min(rawRight, ceiling);
    // A zero-width rectangle renders as a bare vertical line, which reads as a study marker rather
    // than a range - and there is already a vertical line there.
    if (right <= lo) return;

    bands.push({
      date1: dates[lo],
      price1: yMin,
      date2: dates[right],
      price2: yMax,
      x1: lo,
      x2: right,
      /*
       * WHERE THE MEASUREMENT ENDED, which is not where the rectangle ends.
       *
       * <p>Nothing about the DRAWING needs this — `x1`..`x2` is the rectangle and that is
       * unchanged. It is here because the level is not KNOWN until the interval closes: the
       * high and the low are taken from the bars in `lo`..`hi`, so inside that stretch the band
       * is being formed rather than being tested, and the equator is the midpoint of those very
       * bars' own extremes.
       *
       * <p>Chart Analytica reads it to decide where a section becomes testable. Without it the
       * scan counted every measuring interval crossing its own midline — which it does by
       * construction, several times, and which is a look-ahead rather than a finding. The same
       * reasoning gives `TimeBandLines` its `validFrom = band.end + 1` on the engine side.
       */
      xMeasuredTo: hi,
    });
  });
  return bands;
}

/**
 * THE PROJECTED RANGE: a band of the SAME HEIGHT directly above and below each cycle band.
 *
 * <pre>
 *   high + h   ┌──────────────────┐  the upper projection
 *   high       ├──────────────────┤
 *              │  the CYCLE BAND  │  buildCycleBands draws this, with its stroke
 *   low        ├──────────────────┤
 *   low  - h   └──────────────────┘  the lower projection
 *              ^ this pillar        ^ where the next cycle begins
 * </pre>
 *
 * <h2>It inherits the band's span rather than recomputing one</h2>
 *
 * `date1`/`date2` are copied, not re-derived, so "spanning cleanly from the originating time
 * pillar to the next pillar" is true by construction. The span is the one decision in a cycle
 * band that is genuinely subtle — measured over the interval, drawn to the NEXT one, with the
 * final band projected forward — and a second derivation of it would be a second answer to a
 * question this file has already answered carefully.
 *
 * <h2>Each projection is its own rectangle, not one tall box</h2>
 *
 * A single rectangle from {@code low - h} to {@code high + h} would fill the cycle band a second
 * time, doubling its opacity on the one tier that is a MEASUREMENT rather than a projection.
 * Two separate rectangles leave the middle to the band itself; they touch it at its edges and
 * never enter it.
 *
 * <h2>A ZERO-HEIGHT band projects nothing</h2>
 *
 * An interval whose bars all printed one price has no range to project, and two zero-height
 * rectangles stacked on it would be invisible marks that still cost an overlay each.
 *
 * @param {Array<object>} bands the output of {@link buildCycleBands}
 * @param {number} multiples how many band-heights to reach, each way. One, as specified.
 * @returns {Array<object>} two per band — upper first — each carrying the source band's `date1`
 *          so a caller can key an overlay id off it, and a `side` of 'upper' or 'lower'
 */
export function cycleRangeProjections(bands, multiples = 1) {
  if (!Array.isArray(bands)) return [];
  const out = [];
  bands.forEach((band) => {
    if (!band) return;
    const { date1, date2, price1, price2 } = band;
    if (!isNum(price1) || !isNum(price2)) return;
    const low = Math.min(price1, price2);
    const high = Math.max(price1, price2);
    const reach = (high - low) * multiples;
    if (!(reach > 0)) return;
    out.push({ ...band, side: 'upper', sourceDate: date1, date1, date2,
      price1: high, price2: high + reach });
    out.push({ ...band, side: 'lower', sourceDate: date1, date1, date2,
      price1: low - reach, price2: low });
  });
  return out;
}

/**
 * THE HORIZONTAL LINES a cycle band draws: its own two edges, its midline, and the bounds.
 *
 * <h2>Why the EDGES are lines rather than the rectangle's border</h2>
 *
 * A `drawRect` renders as a `markArea`, and ECharts gives a markArea one border for all four
 * sides — so a bordered channel came with VERTICAL CAPS at the pillar it starts on and the one
 * it ends on. Those caps are not a reading: a cycle's left edge is already marked by the
 * vertical pillar standing there, and its right edge is the next pillar. Drawing a second,
 * heavier vertical a pixel away said something the structure does not.
 *
 * <p>So the rectangle keeps its fill and loses its border, and the top and bottom come back as
 * LINES — which is also what makes them the same kind of object as the guides below them, drawn
 * in one pass with one weight hierarchy rather than two mechanisms that have to agree.
 *
 * @param {Array<object>} bands the output of {@link buildCycleBands}
 * @param {number} edgeWidth the stroke the MEASURED edges take
 * @param {number} guideWidth the stroke a derived guide takes
 * @returns {Array<object>} `{date1, date2, price, width, dash, kind}`, bands in order
 */
export function cycleGuideLines(bands, edgeWidth, guideWidth) {
  if (!Array.isArray(bands)) return [];
  const out = [];
  bands.forEach((band) => {
    if (!band) return;
    const { date1, date2, price1, price2 } = band;
    if (!isNum(price1) || !isNum(price2)) return;
    const low = Math.min(price1, price2);
    const high = Math.max(price1, price2);
    const at = (price, width, dash, kind) =>
      out.push({ date1, date2, sourceDate: date1, price, width, dash, kind });
    // THE MEASURED EDGES, at the channel's own weight.
    at(high, edgeWidth, 'solid', 'top');
    at(low, edgeWidth, 'solid', 'bottom');
    // AND THE GUIDES, from the shared geometry both renderers read. A zero-height band yields
    // none, so a one-price interval draws its two edges on one line and nothing else.
    guideLevels(high, low).forEach(({ level, kind, dash }) => at(level, guideWidth, dash, kind));
  });
  return out;
}
